import { createListenerMiddleware } from '@reduxjs/toolkit';

import {
  selectAnchorSectionIds,
  selectAnchorSectionIsAnimating,
  selectAnchorSectionNavigationOptions,
} from '../selectors';
import * as slices from '../slices';

import type {
  AnyAction,
  ListenerEffect,
  PayloadAction,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import type { RootState } from '..';

const getScrollTop = () => document.scrollingElement?.scrollTop ?? 0;

const getOffset = (el: HTMLElement) => {
  const scrollTop = getScrollTop();
  const rect = el.getBoundingClientRect();
  return {
    top: Math.floor(rect.top + scrollTop),
    bottom: Math.floor(rect.bottom + scrollTop),
  };
};

let getState: () => RootState;

const setHash = (
  hash: string,
  { replace = true, state = window.history.state } = {},
) => {
  const url = new URL(window.location.href);
  url.hash = hash;
  window.history[replace ? 'replaceState' : 'pushState'](state, '', url);
  window.dispatchEvent(new PopStateEvent('popstate', { state }));
};

// Cache for section elements and offsets
const sectionCache = new Map<
  string,
  { section: HTMLElement; offset: { top: number; bottom: number } }
>();

const updateSectionCache = (sectionIds: string[]) => {
  sectionCache.clear();
  sectionIds.forEach((sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      sectionCache.set(sectionId, { section, offset: getOffset(section) });
    }
  });
};

const handleScrolled = () => {
  const state = getState();
  const isAnimating = selectAnchorSectionIsAnimating(state);
  if (isAnimating) return;

  const sectionIds = selectAnchorSectionIds(state);
  if (sectionCache.size !== sectionIds.length) {
    updateSectionCache(sectionIds);
  }

  const scrollTop = getScrollTop();
  let selectedSection = null;

  for (const [sectionId, { section, offset }] of sectionCache) {
    if (scrollTop >= offset.top && scrollTop < offset.bottom) {
      selectedSection = section;
      break;
    }
  }

  const hash = window.location.hash.replace(/^#/, '');
  if (selectedSection === null) {
    if (hash) {
      setHash('');
    }
  } else {
    const selectedHash = `#${selectedSection.id}`;
    const selectedOptions = selectAnchorSectionNavigationOptions(
      state,
      selectedSection.id,
    );
    setHash(selectedHash, selectedOptions);
  }
};

const handleAnchorSectionIdsModified: ListenerEffect<
  PayloadAction<unknown>,
  RootState,
  ThunkDispatch<RootState, unknown, AnyAction>,
  unknown
> = (_action, api) => {
  getState = api.getState;

  const prevSectionIds = selectAnchorSectionIds(api.getOriginalState());
  const sectionIds = selectAnchorSectionIds(api.getState());

  if (sectionIds.length !== 0 && prevSectionIds.length === 0) {
    window.addEventListener('scroll', handleScrolled, {
      passive: true,
    });
  } else if (sectionIds.length === 0 && prevSectionIds.length !== 0) {
    window.removeEventListener('scroll', handleScrolled);
  }
};

const anchorSectionsMiddleware = createListenerMiddleware();

anchorSectionsMiddleware.startListening({
  actionCreator: slices.anchorSections.actions.sectionAdd,
  effect: (action, api) => handleAnchorSectionIdsModified(action, api as never),
});

anchorSectionsMiddleware.startListening({
  actionCreator: slices.anchorSections.actions.sectionRemove,
  effect: (action, api) => handleAnchorSectionIdsModified(action, api as never),
});

export const anchorSections = anchorSectionsMiddleware.middleware;
